import { useState, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Plus, Trash2, AlertTriangle } from 'lucide-react'
import type { ContentItem } from '../../api/contentApi'

/* eslint-disable @typescript-eslint/no-explicit-any */
type Payload = Record<string, any>

function payloadIdentity(value: Payload) {
  return JSON.stringify(value, (_key, part) => {
    if (part && typeof part === 'object' && !Array.isArray(part)) {
      return Object.fromEntries(Object.keys(part).sort().map(key => [key, part[key]]))
    }
    return part
  })
}

const CATEGORIES = ['Art', 'Photography', 'Design', 'Installation', 'Sculpture']

interface BlogEditorialEditorProps {
  item: ContentItem
  onSave: (payload: Payload) => void
  saving?: boolean
  onDirtyChange?: (dirty: boolean) => void
}

/**
 * Review surface for a blog_editorial draft.
 *
 * The claims panel is the point of this screen, not a detail. The editorial agent's one
 * hard rule is that every factual assertion traces to something the knowledge graph
 * returned (docs/editorial-agent/ARCHITECTURE.md §1.1), and a curator approving an article
 * is signing off on exactly that. So each claim shows its source next to its text, and a
 * claim whose source is blank is called out rather than rendered as an ordinary row —
 * that is the one defect a reader would otherwise scroll straight past.
 */
export function BlogEditorialEditor({ item, onSave, saving, onDirtyChange }: BlogEditorialEditorProps) {
  const [p, setP] = useState<Payload>((item.payload as Payload) || {})
  useEffect(() => {
    setP((item.payload as Payload) || {})
  }, [item.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onDirtyChange?.(payloadIdentity(p) !== payloadIdentity(item.payload || {}))
  }, [p, item.payload, onDirtyChange])

  const set = (k: string, v: any) => setP((prev) => ({ ...prev, [k]: v }))

  const claims: Payload[] = Array.isArray(p.claims) ? p.claims : []
  const setClaims = (next: Payload[]) => set('claims', next)
  const updateClaim = (i: number, k: string, v: any) =>
    setClaims(claims.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)))
  const removeClaim = (i: number) => setClaims(claims.filter((_, idx) => idx !== i))

  const tags: string[] = Array.isArray(p.tags) ? p.tags : []
  const handles: string[] = Array.isArray(p.featured_artwork_handles) ? p.featured_artwork_handles : []
  const body = String(p.body_markdown || p.bodyMarkdown || '')
  const words = body.trim() ? body.trim().split(/\s+/).length : 0
  const unsourced = claims.filter((c) => !String(c.source || '').trim()).length

  return (
    <div className="space-y-5">
      <Field label="Publish destination">
        <p className="text-sm">{p.publicationDestination === 'academy' ? 'Arthaus Academy' : 'WordPress Journal'}</p>
        <p className="text-xs text-muted-foreground">{p.publicationDestination === 'academy' ? 'Save edits, then approve to publish this story to Academy.' : 'Approving publishes this article to blog.myarthaus.com.'}</p>
      </Field>
      <Field label="Headline">
        <Input value={String(p.headline || '')} onChange={(e) => set('headline', e.target.value)} />
      </Field>

      <Field label="Dek">
        <Textarea rows={2} value={String(p.dek || '')} onChange={(e) => set('dek', e.target.value)} />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Category">
          <select
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
            value={String(p.category || '')}
            onChange={(e) => set('category', e.target.value)}
          >
            <option value="">—</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </Field>
        <Field label="Tags (comma separated)">
          <Input
            value={tags.join(', ')}
            onChange={(e) => set('tags', e.target.value.split(',').map((t) => t.trim()).filter(Boolean))}
          />
        </Field>
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label>Body</Label>
          <span className="text-xs text-muted-foreground">{words} words</span>
        </div>
        <Textarea rows={16} value={body} onChange={(e) => set('body_markdown', e.target.value)} />
      </div>

      {handles.length > 0 && (
        <div className="space-y-1.5">
          <Label>Featured artworks</Label>
          <div className="flex flex-wrap gap-1.5">
            {handles.map((h) => (
              <Badge key={h} variant="secondary" className="font-mono text-xs">{h}</Badge>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <Label>Claims and their sources</Label>
            <p className="text-xs text-muted-foreground mt-0.5">
              Every factual assertion must trace to an artwork handle, a graph concept, or the brief.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setClaims([...claims, { text: '', source: '', confidence: 1 }])}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add claim
          </Button>
        </div>

        {unsourced > 0 && (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              {unsourced} claim{unsourced === 1 ? '' : 's'} with no source. Give each one a source
              or remove the sentence from the body before approving.
            </span>
          </div>
        )}

        {claims.map((c, i) => {
          const missing = !String(c.source || '').trim()
          return (
            <div
              key={i}
              className={`rounded-md border p-3 space-y-2 ${missing ? 'border-amber-300 bg-amber-50/40' : ''}`}
            >
              <Textarea
                rows={2}
                value={String(c.text || '')}
                onChange={(e) => updateClaim(i, 'text', e.target.value)}
                placeholder="The claim exactly as it appears in the body"
              />
              <div className="flex items-center gap-2">
                <Input
                  className="font-mono text-xs"
                  value={String(c.source || '')}
                  onChange={(e) => updateClaim(i, 'source', e.target.value)}
                  placeholder="artwork handle, concept, or brief"
                />
                <Button variant="ghost" size="sm" onClick={() => removeClaim(i)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )
        })}
        {claims.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No claims recorded. An article with no claims has nothing to verify.
          </p>
        )}
      </div>

      <div className="flex justify-end">
        <Button onClick={() => onSave(p)} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  )
}
